import type { ReactNode } from 'react'

/**
 * Иконки макета. Пути скопированы из `prototype/Prototype.dc.html` посимвольно —
 * кроме двух мест, где в самом макете опечатка (отмечены ниже).
 *
 * Библиотеку иконок не берём намеренно: в макете толщина обводки гуляет от 1.6 до 2.6,
 * а бренд-иконки (WhatsApp, Telegram) уникальны. Расхождение было бы видно глазом.
 */
export const ICONS = {
  'chevron-down': <path d="M6 9l6 6 6-6" />,
  'chevron-right': <path d="M9 18l6-6-6-6" />,
  'chevron-left': <path d="M15 18l-6-6 6-6" />,
  // Ввод в работу: текст «поднимается» к гостю. Дословно из ДРУГОГО макета, чем указан в шапке
  // файла: `prototype/instructions_and_messages/Инструкции для гостя.dc.html:386`.
  'arrow-up': (
    <>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
    </>
  ),
  check: <path d="M20 6L9 17l-5-5" />,
  close: <path d="M18 6L6 18M6 6l12 12" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </>
  ),
  /** Полукруг с заливкой — классический индикатор светлой/тёмной темы. */
  theme: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 010 18z" fill="currentColor" stroke="none" />
    </>
  ),
  external: <path d="M7 17L17 7M9 7h8v8" />,
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  /**
   * Опечатка в макете: там `l5-5-5` — не хватает координаты, и стрелка рисуется
   * наполовину (верхний ус отсутствует). Здесь путь дописан до `l5-5-5-5`.
   */
  logout: <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />,

  // Пути — из карточки брони (prototype/Prototype.dc.html:2321) и ленты чата (2116-2200).
  /** Рубль — вид действия «оплата» (плитка 17×17, sw 1.9). */
  ruble: <path d="M7 21V4h6a4 4 0 010 8H7M5 15h8" />,
  /** Ветвление — чип нелинейного участка во флоу брони (13×13, sw 1.9). */
  fork: (
    <>
      <circle cx="6" cy="6" r="2.4" />
      <circle cx="18" cy="6" r="2.4" />
      <circle cx="12" cy="19" r="2.4" />
      <path d="M6 8.4v2a3 3 0 003 3h6a3 3 0 003-3v-2M12 13.4v3.2" />
    </>
  ),
  /** Карандаш — «Редактировать» в шапке карточки брони (13×13, sw 1.9). */
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />,
  /** Конверт — канал Email в секции «Клиент» (15×15, sw 1.7). */
  email: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  /*
   * Телефонная трубка — нарисована нами: в наборе макета глифа телефона нет, а шесть видов канала
   * из двенадцати в справочнике гостей телефонные. ⚠️ Ближайший по смыслу `chat` означает ПЕРЕПИСКУ
   * и уже занят ею — поставить его номеру значило бы обещать диалог там, где есть только цифры.
   */
  phone: (
    <path d="M7 3h3l2 5-2.5 1.5a12 12 0 005 5L16 12l5 2v3a2 2 0 01-2.2 2A17 17 0 015 5.2 2 2 0 017 3z" />
  ),
  /** Искра — режим «ИИ» и карточка черновика консьержа (12×12, sw 1.8). */
  spark: <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15l-1.9-4.1L5.5 9l4.6-1.4z" />,
  /** Речевой пузырь — обобщённая иконка канала/мессенджера (prototype/Prototype.dc.html:1546). */
  chat: <path d="M21 15a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z" />,
  /** Документ с загнутым углом — вложение сообщения в новой ленте чата. Нарисован в языке макета. */
  file: (
    <>
      <path d="M13 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
      <path d="M13 3v6h6" />
    </>
  ),

  // Бренд-иконки залиты, а не обведены: `fill`/`stroke` на самом path перекрывают
  // `fill="none"` / `stroke="currentColor"`, которые ставит компонент Icon.
  // Так же устроена иконка `theme` выше.
  /** WhatsApp (16×16, заливка). */
  whatsapp: (
    <path
      fill="currentColor"
      stroke="none"
      d="M12 2a10 10 0 00-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1012 2zm0 2a8 8 0 11-4.1 14.9l-.3-.2-2.8.7.7-2.7-.2-.3A8 8 0 0112 4zm-3 4c-.3 0-.6.1-.8.4-.3.4-.9 1-.9 2.3s1 2.6 1.1 2.8c.1.2 1.8 2.9 4.5 3.9 2.2.8 2.6.7 3.1.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2-.1-.1-.3-.2-.6-.3l-1.4-.7c-.2-.1-.4-.1-.5.1l-.6.8c-.1.2-.3.2-.5.1-.6-.2-1.4-.5-2.2-1.3-.6-.6-1-1.3-1.2-1.5-.1-.2 0-.4.1-.5l.4-.4c.1-.2.1-.3.2-.5 0-.1 0-.3-.1-.4l-.6-1.5c-.2-.4-.4-.4-.6-.4z"
    />
  ),
  /** Telegram (16×16, заливка). */
  telegram: (
    <path
      fill="currentColor"
      stroke="none"
      d="M21.9 4.3l-3 14.2c-.2 1-.8 1.2-1.7.8l-4.6-3.4-2.2 2.1c-.2.2-.5.5-.9.5l.3-4.6L18.4 5c.4-.3-.1-.5-.6-.2L7.5 11.3l-4.5-1.4c-1-.3-1-1 .2-1.5L20.6 2.5c.8-.3 1.5.2 1.3 1.8z"
    />
  ),

  // Виды действий `checkin`, `deposit`, `decision` сервер присылает
  // (контракт: BookingDetail.actionsRequired[].kind), а иконок для них в макете НЕТ НИГДЕ —
  // нарисован только `pay` (рубль). Проверено машиной: на экране «Задачи» вообще нет SVG,
  // кроме двух галочек. Тот же случай, что copy/refresh/alert/wifi-off ниже: экрана под них
  // в прототипе не существует. Нарисованы в языке макета — viewBox 24, обводка, без linecap.
  /** Ключ — «заезд»: открыть код, впустить гостя. */
  checkin: (
    <>
      <circle cx="8" cy="15" r="4.2" />
      <path d="M10.9 12L20 3M16.5 6.5L19 9M14 9l2.5 2.5" />
    </>
  ),
  /**
   * Щит — «залог»: деньги удержаны. Первым рисовали сейф (рамка с циферблатом) — на 17px
   * его насечки не видны, и он читался как коробка или фотоаппарат. Проверено глазами.
   */
  deposit: <path d="M12 3l7.5 3v5.2c0 4.6-3.1 7.8-7.5 9.8-4.4-2-7.5-5.2-7.5-9.8V6z" />,
  /**
   * Вопрос в круге — «решение менеджера»: подтвердить или отклонить. Первым рисовали развилку —
   * она оказалась почти неотличима от иконки `fork`, стоящей рядом в той же карточке.
   */
  decision: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.2a2.6 2.6 0 015.1.9c0 1.7-2.6 2.6-2.6 2.6" />
      <path d="M12 16.8h.01" />
    </>
  ),

  // Иконок ниже в макете нет: они нужны экрану смерти и оффлайн-баннеру,
  // а этих экранов в прототипе не существует — они из production-обвязки.
  // Нарисованы в том же языке: viewBox 24, обводка, без заливки.
  /**
   * Точка. Нужна плитке системного события с нейтральным тоном: галка там означала бы «всё
   * хорошо», а нейтральное событие ничего не утверждает — оно просто произошло. Своя, макета
   * для неё нет; но точка — родной элемент этого языка (статусные точки в чипах, навигации
   * и уведомлениях), так что новой сущности не заводим.
   */
  dot: <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />,

  // Шахматка. Путь — из угловой ячейки полотна (prototype/Prototype.dc.html:590).
  /** Три столбика — «Группировка» в углу колонки объектов (13×13, sw 1.9). */
  columns: <path d="M3 21V8l6-4v17M9 21V4l6 4v13M15 21v-9l6-4v13" />,
  /** Воронка — «Фильтры» в тулбаре шахматки (13×13, sw 1.9). */
  filter: <path d="M3 5h18l-7 8v6l-4-2v-4z" />,
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 11-2.64-6.36" />
      <path d="M21 4v5h-5" />
    </>
  ),
  alert: (
    <>
      <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L14.7 3.9a2 2 0 00-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  /* Кружок с «i» (prototype/Prototype.dc.html:2336). Отдельно от `alert`: тот — треугольник, то
     есть предупреждение, а это пояснение к тому, что и так работает как задумано. */
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M2 2l20 20" />
      <path d="M8.5 16.4a5 5 0 017 0" />
      <path d="M5 12.9a10 10 0 015.2-2.7" />
      <path d="M2 8.8a16 16 0 014.5-2.6" />
      <path d="M13.8 10.2a10 10 0 015.2 2.7" />
      <path d="M17.5 6.2A16 16 0 0012 5" />
      <path d="M12 20h.01" />
    </>
  ),
  /** «Посмотреть глазами гостя» — кнопка живого экрана (prototype/Prototype.dc.html:2359). */
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),

  /*
   * Виды частей текста — плитка 26×26 в шапке карточки части
   * (prototype/Prototype.dc.html:934-938). Рисуются толщиной 1.9, как в макете.
   *
   * «Фото» и «Видео» делят ОДИН глиф — так в макете (`b.isMedia` — это `image||video`, :937).
   */
  'part-text': <path d="M4 7V5h16v2M9 5v14M15 19H9" />,
  'part-link': (
    <>
      <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" />
      <path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
    </>
  ),
  'part-map': (
    <>
      <path d="M21 10c0 6-9 12-9 12S3 16 3 10a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  'part-media': (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M21 16l-5-5L5 20" />
    </>
  ),
  'part-rating': <path d="M12 3l2.6 5.6 6 .8-4.4 4.2 1.1 6-5.3-3-5.3 3 1.1-6L3.4 9.4l6-.8z" />,
  /** Удаление части — подвал раскрытой карточки (prototype/Prototype.dc.html:1010). */
  trash: <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />,
  /* Стрелка в полку — «залить таблицей». Взято у кнопки тулбара (`:1352`); у кнопки в подвале
     карточки (`:1439`) тот же глиф с полкой на 2px ниже (`M4 21h16`) — берём один, а не два. */
  'import-table': <path d="M12 3v12M8 11l4 4 4-4M4 19h16" />,
  /**
   * Три полосы — перечень. Заголовок раздела меню бота им помечался РАНЬШЕ (по прежнему макету,
   * `prototype/Prototype.dc.html:921`); теперь там папка, а единственный потребитель этой иконки —
   * список кодов и данных в карточке квартиры.
   */
  menu: <path d="M4 6h16M4 12h16M4 18h10" />,
  /**
   * Раздел меню бота — папка (`Инструкции для гостя.dc.html:501`). Метит заголовок раздела в
   * редакторе текста: за ним у гостя ОТДЕЛЬНЫЙ ЭКРАН, и папка говорит это точнее, чем три полосы
   * `menu`, с которыми заголовок выглядел обычным подзаголовком списка.
   */
  folder: <path d="M4 7h6l2 2h8v9H4z" />,
  /**
   * Квартира — заметка «это отдельный текст квартиры» в редакторе
   * (`Инструкции для гостя.dc.html:431`). Дословно из макета, два пути.
   */
  home: (
    <>
      <path d="M4 20V9l8-5 8 5v11" />
      <path d="M9 20v-6h6v6" />
    </>
  ),
  /**
   * «Ещё» — вертикальное многоточие, дословно из макета (`Инструкции для гостя.dc.html:187`).
   * Открывает меню действий: у строки библиотеки, у строки разреза и у заголовка раздела в
   * редакторе. Не путать с `menu` выше — тот три ПОЛОСЫ и означает перечень, а не действия.
   */
  more: (
    <>
      <circle cx="12" cy="5" r=".6" />
      <circle cx="12" cy="12" r=".6" />
      <circle cx="12" cy="19" r=".6" />
    </>
  ),
  /**
   * Ручка перетаскивания — шесть точек в два столбца (`Инструкции для гостя.dc.html:522`).
   *
   * В макете это ЗАЛИТЫЕ кружки `r="1.4"`, у нас — обведённые `r=".6"`, как у соседнего `more`:
   * `Icon` рисует всё одним `fill="none" stroke="currentColor"`, и обводка толщиной 2.4 смыкается в
   * точку внешнего радиуса 1.8 — то есть НЕ такую же, а примерно на треть крупнее макетной. Разница
   * на 13px незаметна, а заливка потребовала бы исключения в общем компоненте ради одного глифа,
   * стоящего в одной строке с `more`, — и два соседних многоточия рисовались бы по разным правилам.
   */
  grip: (
    <>
      <circle cx="9" cy="6" r=".6" />
      <circle cx="15" cy="6" r=".6" />
      <circle cx="9" cy="12" r=".6" />
      <circle cx="15" cy="12" r=".6" />
      <circle cx="9" cy="18" r=".6" />
      <circle cx="15" cy="18" r=".6" />
    </>
  ),
  /** Закрытые данные (коды доступа) — чип вставки в палитре (prototype/Prototype.dc.html:997). */
  lock: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </>
  ),
  /*
   * ⚠️ Этот глиф НАШ, а не макетный: части «Действие» в макете нет вовсе (ни в `g3TYPES`, ни в
   * `cBlockMeta`), а в контракте она есть — со снятой функцией (6.15, «не работает ни в одном
   * канале»). Рисуем капсулу-кнопку: форма честно называет вид, не занимая чужой глиф.
   */
  'part-action': <rect x="3" y="9" width="18" height="7" rx="3.5" />,
  /**
   * Панель гостя — метит шапку панели брони в редакторе
   * (`Сообщения для гостя.dc.html:452`, дословно два пути). Заполненная полоса сверху — сама шапка,
   * короткая черта под ней — то, что панель собирает дальше сама.
   */
  panel: (
    <>
      <path d="M4 6h16v5H4z" />
      <path d="M4 15h9" />
    </>
  ),
  /**
   * Отправка — метит природу обычного сообщения в редакторе
   * (`Сообщения для гостя.dc.html:2350`, дословно). Рядом с `panel` они и различают две природы:
   * это улетает, то стоит на месте.
   */
  send: <path d="M4 12l16-8-6 16-2-6z" />,
} satisfies Record<string, ReactNode>

export type IconName = keyof typeof ICONS
